import { useCallback, useRef, useState } from "react";

export interface TaskSelection {
  selectedIds: Set<number>;
  /** Shift-click extends from the last plain click, within the visible page. */
  toggle: (taskId: number, shiftKey: boolean, pageIds: number[]) => void;
  togglePage: (pageIds: number[]) => void;
  clear: () => void;
  /** Replace the selection wholesale — used by "Retry failed". */
  replace: (taskIds: number[]) => void;
}

/**
 * Which images the next batch will run on.
 *
 * Local state, not URL state: the rest of the annotate screen's view (page,
 * filter, sort, the open image) lives in search params because it is worth
 * sharing and restoring, but a selection is a transient intent — a link
 * carrying two hundred ids would be neither.
 *
 * Deliberately NOT cleared when the page changes, so an operator can walk
 * several pages building one batch. The caller clears it when the filter or
 * sort changes, because "everything I picked" stops meaning anything once the
 * set it was picked from is replaced.
 */
export const useTaskSelection = (): TaskSelection => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const anchorRef = useRef<number | null>(null);

  const toggle = useCallback(
    (taskId: number, shiftKey: boolean, pageIds: number[]) => {
      setSelectedIds((current) => {
        const next = new Set(current);
        const anchor = anchorRef.current;
        const from = anchor === null ? -1 : pageIds.indexOf(anchor);
        const to = pageIds.indexOf(taskId);

        // A range needs both ends on the page the operator is looking at.
        // With a cross-page selection the anchor is routinely somewhere else,
        // and "everything between two rows you cannot see" is not a gesture
        // anyone means to make — so that degrades to a plain toggle.
        if (shiftKey && from !== -1 && to !== -1) {
          const [start, end] = from < to ? [from, to] : [to, from];
          // The anchor's own state wins for the whole range, matching every
          // other list: shift-clicking after a deselect deselects the range.
          const selecting = next.has(anchor!);
          for (const id of pageIds.slice(start, end + 1)) {
            if (selecting) next.add(id);
            else next.delete(id);
          }
          return next;
        }

        if (next.has(taskId)) next.delete(taskId);
        else next.add(taskId);
        anchorRef.current = taskId;
        return next;
      });
    },
    [],
  );

  const togglePage = useCallback((pageIds: number[]) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      const allSelected = pageIds.length > 0 && pageIds.every((id) => next.has(id));
      for (const id of pageIds) {
        if (allSelected) next.delete(id);
        else next.add(id);
      }
      return next;
    });
    anchorRef.current = null;
  }, []);

  const clear = useCallback(() => {
    setSelectedIds(new Set());
    anchorRef.current = null;
  }, []);

  const replace = useCallback((taskIds: number[]) => {
    setSelectedIds(new Set(taskIds));
    anchorRef.current = null;
  }, []);

  return { selectedIds, toggle, togglePage, clear, replace };
};
